import {Button, FormControlLabel, Paper, Switch} from '@material-ui/core';
import {Async, Loaded, toEither} from 'Core/models/Async';
import {ap as apEither, fold as foldEither, right} from 'fp-ts/lib/Either';
import {constTrue, identity, not} from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {Lens} from 'monocle-ts';
import React, {useEffect} from 'react';
import {connect} from 'react-redux';
import {RouteComponentProps, withRouter} from 'react-router';
import {ReducerState} from 'rootReducer';
import {renderAsync} from 'Shared/components/RenderAsync';
import RequiredTextInput from 'Shared/components/RequiredTextInput';
import ValidatableEmailInput from 'Shared/components/ValidatableEmailInput';
import ValidatablePhoneInput from 'Shared/components/ValidatablePhoneInput';
import ValidatableTextInput from 'Shared/components/ValidatableTextInput';
import {NoOp} from 'Shared/utilities/IOUtilities';
import * as VipActions from 'Users/ducks/vipUser';
import {eqVip, ErrorVip, matchVip, ValidVip, Vip} from 'Users/models/Vip';
import {UserRouteConstants} from 'Users/Routes';
import './AgentManualEntry.sass';
import './VipEntry.sass';

export type VipUserProps = {
  vip: Async<Vip>;
  originalVip: O.Option<Async<Vip>>;
} & typeof VipActions &
  RouteComponentProps<{userId: string}>;

const vipLens = Lens.fromProp<Vip>();

const loadVipIfNeeded = (asyncVip: Async<Vip>, getUser: (userId: string) => void) =>
  pipe(
    toEither(asyncVip),
    foldEither(
      () => NoOp,
      user =>
        matchVip(user, {
          ErrorVip: () => NoOp,
          InvalidVip: () => NoOp,
          SavedVip: () => NoOp,
          UnvalidatedVip: () => getUser,
          ValidVip: () => NoOp,
        })
    )
  );

export const VipUserEntry = (props: VipUserProps) => {
  const vipUserId = O.fromNullable(props.match.params.userId);
  const isEdit = O.isSome(vipUserId);
  const formLabel = pipe(
    vipUserId,
    O.fold(
      () => 'Add',
      () => 'Edit'
    )
  );
  const changeMapping = (vipUser: Vip) => {
    props.changeVipUser(
      new Loaded(
        ValidVip.create(
          vipUser.firstName,
          vipUser.lastName,
          vipUser.username,
          vipUser.phoneNumber,
          vipUser.email,
          vipUser.isAhl,
          vipUser.isNewYork,
          vipUser.isExternalAdministrator
        )
      )
    );
  };

  useEffect(() => {
    pipe(vipUserId, O.fold(NoOp, loadVipIfNeeded(props.vip, props.getVipUser)));

    return () => {
      props.clearVipUser();
    };
  }, []);

  const renderVipForm = (vip: Vip) => (originalVip: O.Option<Vip>) => {
    const equalsCurrentVip = (otherVip: Vip) => eqVip.equals(vip, otherVip);
    const hasVipChanged = pipe(originalVip, O.fold(constTrue, not(equalsCurrentVip)));
    const allowSave = !(vip instanceof ValidVip) || !hasVipChanged;

    return (
      <Paper className="agent-entry">
        <h2 className="agent-entry__title">
          {formLabel}
          &nbsp;Vip
        </h2>
        <p className="agent-entry__description">Please enter the VIP user's information below.</p>
        <form className="agent-entry__form">
          <RequiredTextInput
            label="First Name"
            className="agent-entry__field"
            onChange={firstName => changeMapping(vipLens('firstName').set(firstName)(vip))}
            value={vip.firstName}
          />
          <RequiredTextInput
            label="Last Name"
            className="agent-entry__field"
            onChange={lastName => changeMapping(vipLens('lastName').set(lastName)(vip))}
            value={vip.lastName}
          />
          <ValidatableTextInput
            label="User Name"
            disabled={isEdit}
            className="agent-entry__field"
            onValidate={props.validateUsername}
            value={vip.username}
          />
          <ValidatableEmailInput
            className="agent-entry__field"
            onValidate={props.validateEmail}
            label="Email"
            value={vip.email}
          />
          <ValidatablePhoneInput
            className="agent-entry__field"
            value={vip.phoneNumber}
            label="Phone"
            onValidate={props.validatePhone}
          />
          <div className="vip-entry__access-permissions">
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={vip.isAhl}
                  onChange={event => {
                    changeMapping(vipLens('isAhl').set(event.target.checked)(vip));
                  }}
                />
              }
              label="American Heritage Life Access?"
            />
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={vip.isNewYork}
                  onChange={event => {
                    changeMapping(vipLens('isNewYork').set(event.target.checked)(vip));
                  }}
                />
              }
              label="New York Access?"
            />
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={vip.isExternalAdministrator}
                  onChange={event => {
                    changeMapping(vipLens('isExternalAdministrator').set(event.target.checked)(vip));
                  }}
                />
              }
              label="Is External Administrator?"
            />
          </div>
          <div className="agent-entry-form__buttons">
            <div className="agent-entry-form__return-button">
              <Button
                variant="contained"
                onClick={() => {
                  props.history.push(UserRouteConstants.USERS);
                }}
              >
                Cancel
              </Button>
            </div>
            <Button
              className="agent-entry-form__add-button"
              color="primary"
              type="submit"
              variant="contained"
              disabled={allowSave}
              onClick={() => {
                props.saveVipUser(
                  vip as ValidVip,
                  pipe(
                    vipUserId,
                    O.fold(() => '', identity)
                  )
                );
              }}
            >
              Save
            </Button>
          </div>
          <p className="agent-entry-form__error-message">{vip instanceof ErrorVip && vip.errors.message}</p>
        </form>
      </Paper>
    );
  };

  const eitherVip = toEither(props.vip);
  const originalVipOption = pipe(
    props.originalVip,
    O.fold(
      () => O.none,
      originalVip => O.fromEither(toEither(originalVip))
    )
  );

  return pipe(
    right(renderVipForm),
    apEither(eitherVip),
    apEither(right(originalVipOption)),
    foldEither(renderAsync, identity)
  );
};

const mapStateToProps = (state: ReducerState) => ({
  vip: state.user.vipUser.vip,
  originalVip: state.user.vipUser.originalVip,
  userClaims: state.user.profile.userClaims,
});

export default withRouter(
  connect(mapStateToProps, {
    ...VipActions,
  })(VipUserEntry)
);
